import { useCallback, useEffect, useState } from 'react';
import type {
  IPersonAutoMatchMultiResult,
  IPersonAutoMatchResult,
  IPersonDetail,
  IPersonListItem,
  IPersonListResponse,
} from '@skystone/shared';
import { personApi } from '@/services/person-api';
import { getErrorMessage } from '@/lib/utils';
import { PageContainer } from '@/components/layout';
import { PageHeader, PageLoading, ErrorBanner } from '@/components/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { AutoMatchStatusCard } from '@/components/people/AutoMatchStatusCard';
import { PersonListTable } from '@/components/people/PersonListTable';
import { PersonDetailDialog } from '@/components/people/PersonDetailDialog';
import { UnmatchedTab } from '@/components/people/UnmatchedTab';
import { PreviouslyUnlinkedTab } from '@/components/people/PreviouslyUnlinkedTab';
import { GitHubAdminTab } from '@/components/people/GitHubAdminTab';
import { SsoLoginsTab } from '@/components/people/SsoLoginsTab';
import { OrgGraphTab } from '@/components/people/OrgGraphTab';
import { OrgTreeTab } from '@/components/people/OrgTreeTab';

type Tab =
  | 'people'
  | 'sso-logins'
  | 'unmatched'
  | 'previously-unlinked'
  | 'github'
  | 'org-graph'
  | 'org-tree';

// PersonListTable does filter/sort/paginate client-side via DataTable, so the
// page expects to receive the full dataset in one fetch. The backend's
// listPersons defaults pageSize to 50, which would truncate the visible list
// to 50 of N rows. Pass a generous cap that's well above any plausible org
// size so the DataTable sees every Person. If the directory ever crosses
// this number, revisit by moving the DataTable into server-driven mode.
const PEOPLE_LIST_PAGE_SIZE = 10000;

export function AdminPeoplePage() {
  const [activeTab, setActiveTab] = useState<Tab>('people');

  const [listResponse, setListResponse] = useState<IPersonListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // `includeTombstoned` + `includeBots` stay top-level toggles because they
  // change WHAT the backend returns — fetching every tombstoned/bot row just
  // to client-filter them out would be wasteful. All other filtering moved to
  // per-column controls inside `PersonListTable` (PRODOPSAI-453).
  const [includeTombstoned, setIncludeTombstoned] = useState(false);
  const [includeBots, setIncludeBots] = useState(false);
  // Reveals inactive-departed Persons (deactivated by the Entra dropout sweep,
  // not merge-tombstoned). Independent of the two toggles above (PRODOPSAI-489).
  const [includeInactive, setIncludeInactive] = useState(false);

  const [isRunningMatch, setIsRunningMatch] = useState(false);
  const [lastMatchResult, setLastMatchResult] = useState<IPersonAutoMatchResult | null>(null);
  const [lastMatchBySource, setLastMatchBySource] =
    useState<IPersonAutoMatchMultiResult | null>(null);

  const [detail, setDetail] = useState<IPersonDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadList = useCallback(
    async (tombstoned: boolean, showBots: boolean, inactive: boolean): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const response = await personApi.list({
          includeTombstoned: tombstoned,
          includeBots: showBots,
          includeInactive: inactive,
          pageSize: PEOPLE_LIST_PAGE_SIZE,
        });
        setListResponse(response);
      } catch (err: unknown) {
        setError(getErrorMessage(err, 'Failed to load people'));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    loadList(false, false, false);
  }, [loadList]);

  const handleToggleTombstoned = (): void => {
    const next = !includeTombstoned;
    setIncludeTombstoned(next);
    loadList(next, includeBots, includeInactive);
  };

  const handleToggleBots = (): void => {
    const next = !includeBots;
    setIncludeBots(next);
    loadList(includeTombstoned, next, includeInactive);
  };

  const handleToggleInactive = (): void => {
    const next = !includeInactive;
    setIncludeInactive(next);
    loadList(includeTombstoned, includeBots, next);
  };

  // Single reload callback shared by every "an action somewhere else
  // finished, please refresh" caller — auto-match completion, the
  // UnmatchedTab and PreviouslyUnlinkedTab callbacks, and the detail
  // dialog's onUpdated/onMerged hooks below.
  const reloadList = useCallback(
    () => loadList(includeTombstoned, includeBots, includeInactive),
    [loadList, includeTombstoned, includeBots, includeInactive],
  );

  const handleRunMatch = async (): Promise<void> => {
    setIsRunningMatch(true);
    setError(null);
    try {
      const response = await personApi.runAutoMatch();
      setLastMatchResult(response.result);
      setLastMatchBySource(response.bySource);
      await reloadList();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to run auto-match'));
    } finally {
      setIsRunningMatch(false);
    }
  };

  // Loads a Person by id and opens the detail dialog. Shared by the People
  // table's row-click and the SSO logins tab's linked-person link. When
  // called from the SSO logins tab we also switch back to the People tab
  // so the dialog renders above its natural home.
  const openPersonDetail = async (personId: string): Promise<void> => {
    setActiveTab('people');
    setDetail(null);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const response = await personApi.get(personId);
      setDetail(response);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load person'));
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSelectPerson = (person: IPersonListItem): Promise<void> => openPersonDetail(person.id);

  const handleCloseDetail = (): void => {
    setDetailOpen(false);
  };

  if (loading && listResponse === null) {
    return <PageLoading />;
  }

  const items = listResponse?.items ?? [];
  const total = listResponse?.total ?? 0;
  const lastRunAt = listResponse?.autoMatch.lastRunAt ?? null;

  return (
    <PageContainer variant="wide" className="space-y-6">
      <PageHeader
        title="People"
        subtitle="Canonical identity across AD and Slack. Read-only in this view."
      />

      <ErrorBanner message={error} />

      <div className="flex gap-1 border-b">
        <button
          type="button"
          onClick={() => setActiveTab('people')}
          className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'people'
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          People
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('sso-logins')}
          className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'sso-logins'
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          SSO logins
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('unmatched')}
          className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'unmatched'
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          Unmatched
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('previously-unlinked')}
          className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'previously-unlinked'
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          Previously unlinked
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('github')}
          className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'github'
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          GitHub
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('org-graph')}
          className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'org-graph'
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          Org Graph
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('org-tree')}
          className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'org-tree'
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          Org Tree
        </button>
      </div>

      {activeTab === 'people' && (
        <>
          <AutoMatchStatusCard
            lastRunAt={lastRunAt}
            lastResult={lastMatchResult}
            bySource={lastMatchBySource}
            isRunning={isRunningMatch}
            onRun={handleRunMatch}
          />

          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle className="text-lg">People ({total})</CardTitle>
                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={includeTombstoned}
                      onChange={handleToggleTombstoned}
                    />
                    <span>Include tombstoned</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={includeBots}
                      onChange={handleToggleBots}
                    />
                    <span>Show bots</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={includeInactive}
                      onChange={handleToggleInactive}
                    />
                    <span>Show inactive</span>
                  </label>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <PersonListTable items={items} onSelect={handleSelectPerson} />
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === 'sso-logins' && <SsoLoginsTab onOpenPersonDetail={openPersonDetail} />}

      {activeTab === 'unmatched' && (
        <UnmatchedTab
          onActionCompleted={reloadList}
        />
      )}

      {activeTab === 'previously-unlinked' && (
        <PreviouslyUnlinkedTab
          onActionCompleted={reloadList}
        />
      )}

      {activeTab === 'github' && <GitHubAdminTab />}

      {activeTab === 'org-graph' && <OrgGraphTab />}

      {activeTab === 'org-tree' && <OrgTreeTab />}

      <PersonDetailDialog
        detail={detailLoading ? null : detail}
        open={detailOpen}
        onClose={handleCloseDetail}
        onUpdated={() => {
          if (detail) {
            handleSelectPerson({
              id: detail.person.id,
              displayName: detail.person.displayName,
              primaryEmail: detail.person.primaryEmail,
              kind: detail.person.kind,
              isActive: detail.person.isActive,
              linkedAccountCount: detail.links.length,
              isTombstoned: detail.person.mergedIntoPersonId !== null,
              entraDisplayName: detail.sourceRefs.entra[0]?.displayName ?? null,
              department: detail.sourceRefs.entra[0]?.department ?? null,
              jobTitle: detail.sourceRefs.entra[0]?.jobTitle ?? null,
              managerName: detail.sourceRefs.entra[0]?.managerName ?? null,
              deactivatedAt: detail.person.deactivatedAt,
            });
            reloadList();
          }
        }}
        onMerged={() => {
          // After a merge, close the detail view and reload the list;
          // the winner is the current Person, which stays visible.
          handleCloseDetail();
          reloadList();
        }}
      />
    </PageContainer>
  );
}
